import CustomFieldContext from '@nile/banyan/src/Context/CustomFieldContext';
import {
  ALL_SHARD_MODULES,
  CUSTOM_FIELDS,
  DetailCard,
  DropdownIndicator,
  getTranslatedText,
  stopPropagation,
  TEXT_LENGTH,
  validateForm,
  PermissionToggle,
  HeaderTag,
  COMMON_CONSTANTS,
  reactSelectInlineError,
  reactSelectErrorStyles
} from '@nile/components';
import nanoid from 'nanoid';
import SecuredField from '@nile/rmm/src/modules/common/components/sub-pages/secured-field';
import { RadioButton, TextField } from '@shopify/polaris';
import { DragHandleMinor } from '@shopify/polaris-icons';
import { Edit } from '@styled-icons/evaicons-solid';
import { Check } from '@styled-icons/fa-solid/Check';
import { Cross } from '@styled-icons/icomoon/Cross';
import { Delete } from '@styled-icons/material';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import DatePicker from 'react-datepicker';
import { FormattedMessage, injectIntl } from 'react-intl';
import Select from 'react-select';
import { ReactSortable } from 'react-sortablejs';
import styled from 'styled-components';
import flattenOptionsError from '@nile/banyan/src/Utils/flattenOptionsError';
import { multiSelectOptionValidator } from '@nile/components/src/Validators/forms/CustomField';
import {
  Heading,
  Checkbox,
  ToggleButton,
  Button,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CascadeTemplateContainer from '@nile/banyan/src/Components/Cascades/CascadeTemplateContainer';
import { DEPENDENT_FIELD_OPTION, getDefaultCustomFields } from './constant';
import { AccountClient } from '@nile/graphql';
import { GET_ELIGIBLE_PARENTS } from '@nile/banyan/src/Graphql/Mutations/ticket';

const Permissions = styled.div`
  margin-top: 40px;
  .Polaris-Choice {
    margin-top: 10px;
  }
`;

const Choices = styled.div`
  .Polaris-Choice {
    margin-top: 10px;
  }
  .Polaris-Connected {
    margin-top: 15px;
  }
  .Polaris-TextField {
    border: 1px solid #dedede;
    margin-bottom: 0 !important;
    padding: 5px;
  }
  .Polaris-TextField__Backdrop::after {
    border-bottom: 0;
  }
  .Polaris-TextField__Backdrop {
    border-bottom: 0;
  }
`;

const SortItem = styled.div`
  padding: 8px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 16px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  .actions {
    visibility: hidden;
  }
  .actions-visible {
    visibility: visible;
  }
  &:hover > .actions {
    visibility: visible;
  }
`;

const OptionList = styled.div`
  border-radius: 2px;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 16px;
  border: 1px solid #dedede;
  svg {
    height: 15px;
    opacity: 0.3;
    margin-left: 4px;
  }
  .hover-grey:hover {
    background-color: #f2f2f7;
  }
`;

function NewInputFeild(props) {
  const [checked, setChecked] = useState(props.context.value.showToClient);
  const [mandatoryOnCreation, setMandatoryOnCreation] = useState(
    props.context.value?.mandatoryOnCreate
  );
  const [mandatoryOnClosure, setMandatoryOnClosure] = useState(
    props.context.value?.mandatoryOnClosure
  );

  const handleShowToClients = useCallback((newChecked) => {
    props.context.updateCustomField(newChecked, 'showToClient');
    setChecked(newChecked);
  }, []);

  const handleMandatoryWhenCreating = useCallback((newChecked) => {
    props.context.updateCustomField(newChecked, 'mandatoryOnCreate');
    setMandatoryOnCreation(newChecked);
  }, []);

  const handleMandatoryWhenClosure = useCallback((newChecked) => {
    props.context.updateCustomField(newChecked, 'mandatoryOnClosure');
    setMandatoryOnClosure(newChecked);
  }, []);

  return (
    <Permissions className="flex fcol" name="permission">
      <PermissionToggle
        showToClients={checked}
        setShowToClients={handleShowToClients}
        mandatoryOnCreation={mandatoryOnCreation}
        setMandatoryOnCreation={handleMandatoryWhenCreating}
        mandatoryOnClosure={mandatoryOnClosure}
        setMandatoryOnClosure={handleMandatoryWhenClosure}
        disableEdit={props.disableEdit}
        showWarning={props.showWarning}
        hideMandatoryOnClosure={props.hideMandatoryOnClosure}
        hideMandatoryOnCreation={props.hideMandatoryOnCreation}
        hideShowToClients={props?.context?.value?.fieldCategory === 'DEFAULT'}
      />
    </Permissions>
  );
}

function ChoiceList(props) {
  const { intl, isDependentField = false, parentProp = {} } = props;

  const [allOptions, setAllOptions] = useState(
    props.context?.value.options || []
  );

  const [options, setOptions] = useState(
    props.context?.value?.options?.map((el, idx) => {
      return {
        name: el?.value || el?.name || el,
        value: el?.value || el,
        id: el?.id || null,
        order: el?.order,
        parentOption: el?.parentOption || null
      };
    }) || []
  );

  const optionRef = useRef([]);
  optionRef.current = options;

  const [value, setValue] = useState({ name: '', value: '' });

  const validateOptions = (options) => {
    // Check if number of options are within limit
    const isSingleSelect = props.type === 'RADIO' || props.type === 'SELECT';
    const optionsTotalLength = isSingleSelect
      ? props.constraints.singleChoiceOptionsLimit
      : props.constraints.multiChoiceOptionsLimit;
    const optionMaxLength = props.constraints.optionCharLength;
    const validationResult = validateForm(
      { options },
      {
        options: multiSelectOptionValidator({
          mandatory: true,
          optionMaxLength,
          optionsTotalLength
        })
      }
    );
    if (validationResult) {
      props.setErrors({
        ...props.error,
        options: flattenOptionsError(validationResult.options)
      });
      return true;
    } else {
      props.setErrors({ ...props.error, options: [] });
      return false;
    }
  };

  const updateOptionValue = (value) => {
    setValue({ name: value, value: value });
  };

  const addOptions = (e) => {
    if (e.which === 13 || e.charCode === 13 || e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (value.value.trim()) {
        let updatedOptions = options.concat([
          {
            ...value,
            parentOption: parentProp?.parent?.id
              ? { id: parentProp?.parent?.id }
              : null,
            id: nanoid(),
            isNewField: true
          }
        ]);
        const hasError = validateOptions(
          isDependentField ? getUpdatedOptions(updatedOptions) : updatedOptions
        );
        if (!hasError) {
          updateOptionsContext(updatedOptions);
          setOptions(updatedOptions);
          setValue({ name: '', value: '' });
        }
      }
    }
  };

  const headerMap = (type) => {
    const options = {
      CHECKBOX: getTranslatedText(
        intl,
        'admin.customField.create.dropdown.checkbox'
      ),
      RADIO: getTranslatedText(intl, 'admin.customField.create.dropdown.radio'),
      SELECT: getTranslatedText(
        intl,
        'admin.customField.create.dropdown.select'
      ),
      MULTI_SELECT: getTranslatedText(
        intl,
        'admin.customField.create.dropdown.multiselect'
      )
    };
    return (
      options[type] ||
      getTranslatedText(intl, 'admin.customField.create.dropdown.dropDown')
    );
  };

  const getUpdatedOptions = (options) => {
    if (isDependentField && !isEmpty(parentProp)) {
      let jsonArray = allOptions;

      jsonArray = jsonArray.filter(
        (option) =>
          (option?.parentOption?.id &&
            option?.parentOption?.id !== parentProp?.parent?.id) ||
          options.some((newItem) => newItem?.id === option?.id)
      );

      options.map((el) => {
        const index = jsonArray.findIndex((obj) => obj?.id === el?.id);
        let newObject = {
          ...el,
          name: el?.value || el?.name || el,
          value: el?.value,
          id: el?.id,
          order: el?.order,
          parentOption: el?.parentOption ? { id: el?.parentOption?.id } : null,
          isNewField: el?.isNewField
        };
        if (index !== -1) {
          jsonArray[index] = newObject;
        } else {
          jsonArray.push(newObject);
        }
      });
      return jsonArray;
    } else {
      return options?.map((el) => ({
        ...el,
        name: el?.value || el?.name || el,
        value: el?.value,
        id: el?.id,
        order: el?.order,
        parentOption: el?.parentOption || null
      }));
    }
  };

  const updateOptionsContext = (options) => {
    const updatedOptions = getUpdatedOptions(options);
    props.context.updateCustomField(updatedOptions, 'options');
  };

  const setList = (list) => {
    const updatedList = list.map((el, idx) => ({
      ...el,
      order: idx
    }));
    updateOptionsContext(updatedList);
    setOptions(updatedList);
  };

  const itemActions = (idx, type) => {
    let tempOptions = _.clone(optionRef.current);
    switch (type) {
      case 'edit':
        tempOptions[idx].setEdit = true;
        tempOptions[idx].tempvalue = tempOptions[idx].value;
        break;
      case 'delete':
        tempOptions.splice(idx, 1);
        updateOptionsContext(tempOptions);
        break;
      case 'save':
        tempOptions[idx].name = tempOptions[idx].tempvalue;
        tempOptions[idx].value = tempOptions[idx].tempvalue;
        tempOptions[idx].setEdit = false;
        updateOptionsContext(tempOptions);
        break;
      case 'undo':
        tempOptions[idx].setEdit = false;
        break;
      default:
        break;
    }
    setOptions(tempOptions);
    validateOptions(tempOptions);
  };

  const onItemChange = (e, idx) => {
    let tempOptions = _.clone(optionRef.current);
    tempOptions[idx].tempvalue = e.target.value;
    setOptions(tempOptions);
  };

  useEffect(() => {
    if (isDependentField && !isEmpty(parentProp)) {
      let currentOptions = props?.context?.value?.options?.filter(
        (el) =>
          el?.parentOption?.id &&
          el?.parentOption?.id === parentProp?.parent?.id
      );
      currentOptions = currentOptions?.map((el) => ({
        ...el,
        name: el?.value || el?.name || el,
        value: el?.value,
        id: el?.id,
        order: el?.order,
        parentOption: el?.parentOption || null
      }));
      setAllOptions(
        props?.context?.value?.options?.map((el) => ({
          ...el,
          name: el?.value || el?.name || el,
          value: el?.value,
          id: el?.id,
          order: el?.order,
          parentOption: el?.parentOption || null
        }))
      );
      setOptions(sortBy(currentOptions, ['order']));
    }
  }, [
    JSON.stringify(parentProp),
    JSON.stringify(props.context?.value.options)
  ]);

  return (
    <Choices
      className={`flex fcol ${isDependentField ? '' : 'mt-30'}`}
      onKeyPress={addOptions}
      onMouseDown={stopPropagation}
    >
      {!isDependentField && (
        <div className="flex mb-10">
          <Heading element="h3">{headerMap(props?.type)}</Heading>
        </div>
      )}
      <TextField
        placeholder={getTranslatedText(
          intl,
          'admin.customField.create.enterChoices'
        )}
        value={value.value}
        onChange={updateOptionValue}
        error={props?.error?.options?.[0]}
      />
      {optionRef.current.length > 0 && (
        <OptionList>
          <ReactSortable
            className="sort-wrapper"
            list={optionRef.current}
            setList={setList}
            animation={200}
            filter=".nonSortDragHandle"
            preventOnFilter={true}
          >
            {optionRef.current.map((item, idx) => (
              <div
                className="flex center hover-grey"
                onMouseDown={stopPropagation}
              >
                <DragHandleMinor fill="var(--sops-reorder-icon-bg)" />
                <SortItem className="w280" key={item + idx}>
                  <div className={`${!item.setEdit && 'ellipsis f11'} `}>
                    {item.setEdit ? (
                      <input
                        onKeyPress={(e) =>
                          e.key === 'Enter' ? itemActions(idx, 'save') : {}
                        }
                        onChange={(value) => onItemChange(value, idx)}
                        value={item.setEdit ? item.tempvalue : item.name}
                        autoFocus
                      />
                    ) : (
                      item.name
                    )}
                  </div>
                  <div
                    className={`${
                      item.setEdit ? 'actions-visible' : 'actions'
                    } nonSortDragHandle`}
                  >
                    {item.setEdit ? (
                      <CheckIcon
                        onClick={(e) => {
                          itemActions(idx, 'save');
                        }}
                      />
                    ) : (
                      <EditIcon
                        onClick={(e) => {
                          itemActions(idx, 'edit');
                        }}
                      />
                    )}
                    {item.setEdit ? (
                      <CrossIcon
                        onClick={(e) => {
                          itemActions(idx, 'undo');
                        }}
                      />
                    ) : (
                      <DeleteIcon
                        onClick={(e) => {
                          itemActions(idx, 'delete');
                        }}
                      />
                    )}
                  </div>
                </SortItem>
              </div>
            ))}
          </ReactSortable>
        </OptionList>
      )}
    </Choices>
  );
}

function RelativeField({
  intl,
  type,
  context,
  constraints,
  error,
  setErrors,
  parentProp,
  isDependentField
}) {
  switch (type) {
    case 'CHECKBOX':
    case 'RADIO':
    case 'SELECT':
    case 'MULTI_SELECT':
      return (
        <ChoiceList
          intl={intl}
          type={type}
          context={context}
          constraints={constraints}
          setErrors={setErrors}
          error={error}
          isDependentField={isDependentField}
          parentProp={parentProp}
        />
      );

    default:
      return null;
  }
}

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: '14'
};

const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
  </div>
);

const indicatorSeparatorStyle = {
  width: 0
};

const IndicatorSeparator = ({ innerProps }) => {
  return <span style={indicatorSeparatorStyle} {...innerProps} />;
};

const getCustomFields = ({
  module,
  intl,
  page = null,
  flags,
  dependantFieldType = false
}) => {
  const DEFAULT_CUSTOM_FIELDS = getDefaultCustomFields(intl, module);

  if (dependantFieldType) {
    return DEFAULT_CUSTOM_FIELDS?.filter((el) =>
      DEPENDENT_FIELD_OPTION?.includes(el?.value)
    );
  }
  if (flags?.enableSecureText && module !== ALL_SHARD_MODULES.invoice) {
    DEFAULT_CUSTOM_FIELDS.push({
      label: getTranslatedText(intl, 'admin.customFields.secureText'),
      value: CUSTOM_FIELDS.secureText,
      id: 10
    });
  }

  if (module === ALL_SHARD_MODULES.client) {
    DEFAULT_CUSTOM_FIELDS.push({
      label: getTranslatedText(intl, 'customField.url.label'),
      value: CUSTOM_FIELDS.url,
      id: 10
    });
  }

  const ASSET_CUSTOM_FIELDS = [...DEFAULT_CUSTOM_FIELDS];
  if (!flags?.enableSecureText && module !== ALL_SHARD_MODULES.invoice) {
    ASSET_CUSTOM_FIELDS.push({
      label: getTranslatedText(intl, 'admin.CustomFields.create.op.secure'),
      value: 'PASSWORD',
      id: 10
    });
  }
  if (page && page === 'IT_DOCUMENT') {
    ASSET_CUSTOM_FIELDS.push({
      label: getTranslatedText(intl, 'admin.CustomFields.create.op.totp'),
      value: CUSTOM_FIELDS.totp,
      id: 12
    });
  }

  switch (module) {
    case 'ASSET_CLASS':
      // Once Module name support added for IT documentent will remove this code
      if (page && page === 'IT_DOCUMENT') {
        ASSET_CUSTOM_FIELDS.push({
          label: getTranslatedText(intl, 'admin.asset.class.rich.text'),
          value: 'RICH_TEXT',
          id: 11
        });
      }
      return ASSET_CUSTOM_FIELDS;
    default:
      return DEFAULT_CUSTOM_FIELDS;
  }
};

function FieldTypeChooser(props) {
  const { intl, module, constraints, page, actions, errors } = props;
  const flags = useFlags();

  const { isDependentField, setIsDependentField } = actions;

  const dependantFieldType = props?.context?.value?.isDependantFieldType;

  const isAssetClassPage = module === 'ASSET_CLASS';

  const hasDepedentOption =
    module === ALL_SHARD_MODULES.ticket &&
    (DEPENDENT_FIELD_OPTION?.includes(props?.context?.value?.fieldType) ||
      dependantFieldType);

  const [value, setFieldType] = useState(
    getCustomFields({ module, intl, page, flags }).find(
      (el) => el.value === props.context.value.fieldType
    )
  );
  const [parentsLoading, setParentsLoading] = useState(false);
  const [availableParents, setAvailableParents] = useState([]);
  const [parent, setParent] = useState(props.context.value?.parentField);

  const dependantFieldEnabled =
    isDependentField || !isEmpty(props.context.value?.parentField);

  const getEligibleParents = () => {
    setParentsLoading(true);
    AccountClient.query({
      query: GET_ELIGIBLE_PARENTS,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NETWORK_ONLY,
      variables: {
        input: module || ALL_SHARD_MODULES.ticket
      }
    })
      .then((res) => {
        if (res?.data?.getEligibleParentFields) {
          const fieldsList = res?.data?.getEligibleParentFields;
          const phFields = fieldsList?.map((field) => ({
            ...field,
            value: getTranslatedText(intl, field?.columnName),
            label: getTranslatedText(intl, field?.label)
          }));
          setAvailableParents(phFields || []);
        }
      })
      .finally(() => setParentsLoading(false));
  };

  const clearParent = (val) => {
    props.context.updateCustomField([], 'options');
    props.context.updateCustomField(val, 'parentEnabled');
    props.context.updateCustomField(null, 'parentField');
    setParent(null);
  };
  const handleChange = useCallback((newValue) => {
    setFieldType(newValue);
    clearParent(false);
    props.context.updateCustomField(newValue.value, 'fieldType');
  }, []);

  const handleParentChange = useCallback((newValue) => {
    setParent(newValue);
    props.context.updateCustomField([], 'options');
    props.context.updateCustomField(newValue.id, 'parentField');
  }, []);

  useEffect(() => {
    if (isDependentField) {
      getEligibleParents();
    }
  }, [isDependentField]);

  useEffect(() => {
    if (dependantFieldType && !isAssetClassPage) {
      setIsDependentField(true);
      setFieldType(
        getCustomFields({ module, intl, page, flags }).find(
          (el) => el?.value === 'SELECT'
        )
      );
      props.context.updateCustomField('SELECT', 'fieldType');
    }
  }, [dependantFieldType]);

  return (
    <div>
      <div className="w350">
        <div id="fieldContentContainer.fieldChooser">
          <Select
            className="rule-fields"
            defaultValue={value}
            value={value}
            onChange={handleChange}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            components={{ IndicatorSeparator, DropdownIndicator }}
            options={getCustomFields({
              module,
              intl,
              page,
              flags,
              dependantFieldType
            })}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            isDisabled={props?.context?.value?.id}
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            name="valueselector"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
          />
        </div>
      </div>
      {hasDepedentOption && (
        <div className="mt40 mb20 w500">
          <div className="flex actr g8">
            {!dependantFieldType && (
              <ToggleButton
                onChange={(val) => {
                  clearParent(val);
                  setIsDependentField((prevState) => !prevState);
                }}
                value={dependantFieldEnabled}
                locked={props?.context?.value?.id}
                size="small"
              />
            )}
            <SVGIcon name="DependentField" />
            <TextStyle>
              {getTranslatedText(intl, 'admin.customFields.dependent.field')}
            </TextStyle>
            {dependantFieldEnabled && (
              <div className="flex fcol">
                <Select
                  className="rule-fields"
                  placeholder={getTranslatedText(
                    intl,
                    'common.admin.depedant.field.choose.parent'
                  )}
                  onChange={handleParentChange}
                  classNamePrefix="value_selector"
                  isDisabled={props?.context?.value?.id}
                  isLoading={parentsLoading}
                  formatGroupLabel={formatGroupLabel}
                  components={{ IndicatorSeparator, DropdownIndicator }}
                  options={availableParents}
                  value={parent || undefined}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  name="valueselector"
                  styles={
                    errors?.parentField?.[0]
                      ? {
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999
                          }),
                          ...reactSelectErrorStyles
                        }
                      : {
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }
                  }
                />
                {errors?.parentField &&
                  reactSelectInlineError(errors?.parentField)}
              </div>
            )}
          </div>
        </div>
      )}
      {!isEmpty(parent) ? (
        <CascadeTemplateContainer
          parentField={parent}
          resourceChild={{
            component: (parentProp) => (
              <div id="fieldContentContainer.selectedField">
                <RelativeField
                  intl={intl}
                  type={value?.value}
                  fields={getCustomFields({ module, intl, flags })}
                  fieldIndex={value?.id}
                  context={props?.context}
                  constraints={constraints}
                  setErrors={props.setErrors}
                  error={props.errors}
                  isDependentField={module === ALL_SHARD_MODULES.ticket}
                  parentProp={parentProp}
                />
              </div>
            )
          }}
        />
      ) : (
        <>
          {dependantFieldEnabled ? (
            <></>
          ) : (
            <div id="fieldContentContainer.selectedField">
              <RelativeField
                intl={intl}
                type={value?.value}
                fields={getCustomFields({ module, intl, flags })}
                fieldIndex={value?.id}
                context={props?.context}
                constraints={constraints}
                setErrors={props.setErrors}
                error={props.errors}
              />
            </div>
          )}
        </>
      )}
      <div className="w350">
        {/* Not showing Visibility for asset class only */}
        {module !== 'ASSET_CLASS' &&
          module !== ALL_SHARD_MODULES.requester &&
          module !== ALL_SHARD_MODULES.companyUser &&
          module !== ALL_SHARD_MODULES.invoice && (
            <NewInputFeild
              intl={intl}
              context={props.context}
              disableEdit={props.disableEdit}
              showWarning={props.showWarning}
              hideMandatoryOnClosure={props.hideMandatoryOnClosure}
              hideMandatoryOnCreation={props.hideMandatoryOnCreation}
            />
          )}
      </div>
    </div>
  );
}

function Preview(props) {
  const { intl, context, constraints } = props;
  const { options } = context.value;
  const [previewValue, setPreviewValue] = useState('');

  useEffect(() => {
    setPreviewValue('');
  }, [context.value.fieldType]);

  const handleOnChange = (val) => {
    setPreviewValue(val);
  };

  const handleSelectionRadioChange = (checked, newVal) => {
    setPreviewValue(newVal);
  };

  const handleSelectionChange = (checked, val) => {
    const newPreviewValue = previewValue ? cloneDeep(previewValue) : [];
    if (!checked) {
      const idx = newPreviewValue.indexOf(val);
      newPreviewValue.splice(idx, 1);
      setPreviewValue(newPreviewValue);
    } else {
      newPreviewValue.push(val);
      setPreviewValue(newPreviewValue);
    }
  };

  const renderPreviewSection = () => {
    switch (context.value.fieldType) {
      case 'TEXT':
        return (
          <TextField
            onChange={handleOnChange}
            value={previewValue}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            maxLength={constraints?.textCharLength || 255}
          />
        );
      case 'PASSWORD':
      case CUSTOM_FIELDS.secureText:
        return (
          <SecuredField
            onChange={handleOnChange}
            value={previewValue}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
          />
        );
      case 'PARAGRAPH':
        return (
          <TextField
            onChange={handleOnChange}
            value={previewValue}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            multiline={4}
          />
        );
      case 'DATE':
        return (
          <DatePicker
            selected={previewValue ? dayjs(previewValue).toDate() : ''}
            onChange={(date) => {
              const nDate = dayjs(new Date(date)).format('YYYY-MM-DD');
              handleOnChange(nDate);
            }}
            placeholderText={'Choose date'}
            dateFormat="yyyy-MM-dd"
          />
        );
      case 'DATETIME':
        return (
          <DatePicker
            selected={previewValue ? dayjs(previewValue).toDate() : ''}
            onChange={(date) => {
              const nDate = dayjs(new Date(date)).format('YYYY-MM-DDTHH:mm:ss');
              handleOnChange(nDate);
            }}
            placeholderText={'Choose date'}
            dateFormat="yyyy-MM-dd h:mm aa"
            showTimeInput
          />
        );
      case 'NUMERIC':
        return (
          <TextField
            onChange={handleOnChange}
            value={previewValue}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            maxLength={constraints?.numericPrecision || 10}
            type="number"
          />
        );
      case 'DECIMAL':
        return (
          <TextField
            onChange={handleOnChange}
            value={previewValue}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            inputMode="decimal"
            type="number"
          />
        );
      case 'RADIO':
        return options.map((item) => {
          const value = item?.value || item;
          return (
            <div>
              <RadioButton
                label={value}
                id={value}
                name={value}
                checked={value === previewValue}
                onChange={handleSelectionRadioChange}
              />
            </div>
          );
        });
      case 'CHECKBOX':
        return options.map((item) => {
          const value = item?.value || item;
          return (
            <div>
              <Checkbox
                label={value}
                id={value}
                name={value}
                checked={previewValue.indexOf(value) > -1}
                onChange={handleSelectionChange}
              />
            </div>
          );
        });
      case 'SELECT':
      case 'MULTI_SELECT':
        const dropdownOptions = options?.map((el) => {
          return {
            name: el?.value || el,
            value: el?.value || el,
            label: el?.value || el
          };
        });
        return (
          <Select
            className="rule-fields"
            defaultValue={''}
            onChange={() => {}}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            components={{ IndicatorSeparator, DropdownIndicator }}
            options={dropdownOptions}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            name="valueselector"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
            isMulti={context.value.fieldType === 'MULTI_SELECT'}
          />
        );
      case 'URL':
        return (
          <Button onClick={() => {}} plain icon={<SVGIcon name="Add" />}>
            {getTranslatedText(intl, 'common.link')}
          </Button>
        );
      default:
        return <></>;
    }
  };
  return (
    <PreviewWrapper>
      <div className="Preview-Wrap">
        <div className="flex fcol">
          <HeaderTag
            title={getTranslatedText(intl, 'admin.CustomFields.h2.preview')}
          />
          <div className="ml-12 mt-10 pb10">{context.value.label}</div>
          <div className="ml-12 custom-field-preview">
            {renderPreviewSection()}
          </div>
        </div>
      </div>
    </PreviewWrapper>
  );
}

function FieldMainContainer(props) {
  const {
    intl,
    hidePreview,
    errors,
    module,
    mode,
    page,
    hideCommonCustomField
  } = props;
  const context = useContext(CustomFieldContext);
  const [isDependentField, setIsDependentField] = useState(false);

  const updateName = (label) => {
    context.updateCustomField(label, 'label');
  };
  const updateDescription = (desc) => {
    context.updateCustomField(desc, 'description');
  };
  const actions = {
    updateName: updateName,
    updateDescription: updateDescription,
    isDependentField,
    setIsDependentField
  };

  const isAssetClassPage = module === 'ASSET_CLASS' && page === 'ASSET_CLASS';
  const showCommonCustomField = isAssetClassPage && !hideCommonCustomField;
  const parent = context?.value?.parentField || {};
  return (
    <>
      <CustomFieldWrapper className="g10">
        <div className="flex col full-width" data-qa="fieldLabel">
          <DetailCard
            meta={{
              title: getTranslatedText(intl, 'admin.CustomFields.h2.title')
            }}
            data={context.value}
            notSectioned
            disableActive
            editDescription
            actions={actions}
            keyValue="label"
            errors={errors?.label}
            noCard
            nameLength={TEXT_LENGTH.size_60}
            descriptionLength={TEXT_LENGTH.size_6}
            noHeader
            noInputBorder
            disableEdit={props.disableEdit}
            showWarning={props.showWarning}
          />
          <div className="mt-20" data-qa="fieldType">
            <FieldTypeChooser
              intl={intl}
              context={context}
              actions={actions}
              {...props}
              errors={errors}
              constraints={props.constraints || {}}
              disableEdit={props.disableEdit}
              showWarning={props.showWarning}
            />
          </div>
        </div>
        {!hidePreview && !isDependentField && isEmpty(parent) && (
          <div className="preview-wrapper">
            <Preview
              intl={intl}
              constraints={props?.constraints || {}}
              context={context}
            />
          </div>
        )}
      </CustomFieldWrapper>
      {showCommonCustomField && (
        <div className="flex mt-40 actr">
          <div className="mr-10 flex">
            <ToggleButton
              size="small"
              onChange={() =>
                context.updateCustomField(
                  !context?.value?.commonWorkStationField,
                  'commonWorkStationField'
                )
              }
              value={context?.value?.commonWorkStationField || false}
              locked={mode === 'edit' || false}
            />
          </div>
          <div>
            <FormattedMessage id="rmm.asset.common.custom.field.label" />
          </div>
        </div>
      )}
    </>
  );
}

function FieldContentContainer(props) {
  const { intl, module, page, mode } = props;

  return (
    <>
      <FieldMainContainer
        intl={intl}
        hidePreview={props.hidePreview}
        setErrors={props.setErrors}
        errors={props.errors}
        constraints={props.constraints}
        module={module}
        page={page}
        disableEdit={props.disableEdit}
        showWarning={props.showWarning}
        mode={mode}
        hideCommonCustomField={props.hideCommonCustomField}
        hideMandatoryOnClosure={props.hideMandatoryOnClosure}
        hideMandatoryOnCreation={props.hideMandatoryOnCreation}
      />
    </>
  );
}

const CustomFieldWrapper = styled.div`
  display: flex;
  .Polaris-Card {
    box-shadow: none !important;
    padding: 0 !important;
    width: 50%;
  }
  .detail-card-text {
    margin-top: 12px;
  }
  .detail-card-text .Polaris-TextField__Input {
    font-size: 20px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 24px;
  }
  .detail-card-description {
    width: 100%;
  }
`;

const PreviewWrapper = styled.div`
  margin-top: 60px;
  width: 50%;
  .Polaris-Card {
    margin: 0 !important;
    border: 1px solid #dedede;
    border-radius: 8px;
    background-color: #ffffff;
    width: 100%;
    padding: 12px !important;
  }
`;

const EditIcon = styled(Edit)`
  height: 20px !important;
  color: black;
`;

const DeleteIcon = styled(Delete)`
  height: 20px !important;
  color: black;
  margin-left: 8px;
`;

const CheckIcon = styled(Check)`
  height: 15px !important;
  color: black;
`;

const CrossIcon = styled(Cross)`
  height: 13px !important;
  color: black;
  margin-left: 10px !important;
`;

export default injectIntl(FieldContentContainer);
